import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';

export type PasswordStrength = 'none' | 'weak' | 'medium' | 'strong';

export interface PasswordRuleState {
  length: boolean;
  upper: boolean;
  lower: boolean;
  digit: boolean;
  symbol: boolean;
}

/**
 * @description Reusable live password-strength meter that mirrors the server-side
 * policy (min 10 chars, upper, lower, digit, symbol). Renders a meter bar and a
 * per-rule check-off list. Pure presentational component — does not validate forms.
 */
@Component({
  standalone: false,
  selector: 'app-password-strength',
  templateUrl: './password-strength.component.html',
  styleUrls: ['./password-strength.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordStrengthComponent implements OnChanges {
  @Input() password: string | null = '';

  rules: PasswordRuleState = {
    length: false,
    upper: false,
    lower: false,
    digit: false,
    symbol: false,
  };

  strength: PasswordStrength = 'none';
  satisfiedCount = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if ('password' in changes) {
      this.evaluate(this.password ?? '');
    }
  }

  /**
   * @description Evaluates the password against each rule and derives an overall strength.
   * @param {string} value - The current password value.
   * @returns {void}
   */
  private evaluate(value: string): void {
    this.rules = {
      length: value.length >= 10,
      upper: /[A-Z]/.test(value),
      lower: /[a-z]/.test(value),
      digit: /\d/.test(value),
      symbol: /[^A-Za-z0-9]/.test(value),
    };

    this.satisfiedCount =
      (this.rules.length ? 1 : 0) +
      (this.rules.upper ? 1 : 0) +
      (this.rules.lower ? 1 : 0) +
      (this.rules.digit ? 1 : 0) +
      (this.rules.symbol ? 1 : 0);

    if (!value) {
      this.strength = 'none';
    } else if (this.satisfiedCount <= 2) {
      this.strength = 'weak';
    } else if (this.satisfiedCount <= 4) {
      this.strength = 'medium';
    } else {
      this.strength = 'strong';
    }
  }

  /**
   * @description Gets the meter fill percentage for the progress bar.
   * @returns {number} Percentage between 0 and 100.
   */
  get meterPercent(): number {
    return (this.satisfiedCount / 5) * 100;
  }
}
